import { useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import { getApiUrl } from "../../../../config/api";
import {
  formatUsdWithCode,
} from "../../../../components/usageBilling/usageFormat";

import LimitAmountField, { useLimitAmount } from "../../../../components/usageBilling/LimitAmountField";

// Spend per website chatbot for the selected cycle (GET /admin/bots), and each
// bot's own monthly limit. Every visitor of a bot shares its limit; the bots
// belong to no group. Every existing bot is listed, used or not, so a limit can
// be set before its first visitor; a deleted bot shows while it has usage in
// the cycle, and its limit can no longer be changed.
export default function BotSpendTable({
  bots,
  error,
  onRetry,
  isCurrent,
  enforced,
  canEdit,
  currentUser,
  onSaved,
  language,
  formatMoney = formatUsdWithCode,
  currency = "USD",
  rateState,
}) {
  const zh = language === "zh";
  const [editing, setEditing] = useState(null);
  const rows = bots || [];
  const maxSpend = Math.max(...rows.map((b) => b.cycle_used_usd), 0);
  const th =
    "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider";
  const td = "px-4 py-3 text-sm text-gray-700 whitespace-nowrap";

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {zh ? "各網站聊天機器人花費" : "Spend per website chatbot"}
        </h3>
        <p className="text-sm text-gray-500">
          {zh
            ? "每個聊天機器人有自己的每月額度，所有訪客共用。沒有設定額度的聊天機器人不受限制。"
            : "Each chatbot has its own monthly limit that all its visitors share. A chatbot without a limit is unlimited."}
        </p>
      </div>

      {error ? (
        <div className="flex items-center justify-between gap-3 m-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>
            {zh ? "無法載入聊天機器人用量：" : "Couldn't load chatbot usage: "}
            {error}
          </span>
          <button
            type="button"
            onClick={onRetry}
            className="shrink-0 font-medium underline"
          >
            {zh ? "重試" : "Retry"}
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={th}>{zh ? "聊天機器人" : "Chatbot"}</th>
                <th className={th}>
                  {isCurrent
                    ? zh
                      ? "每月額度"
                      : "Monthly limit"
                    : zh
                      ? "每月額度（目前）"
                      : "Monthly limit (current)"}
                </th>
                <th className={th}>{zh ? "本週期花費" : "Cycle spend"}</th>
                {isCurrent && (
                  <th className={th}>{zh ? "本週花費／額度" : "This week / quota"}</th>
                )}
                <th className={th}>{zh ? "提問次數" : "Questions"}</th>
                {canEdit && <th className={th} />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {!bots && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                    {zh ? "正在載入..." : "Loading..."}
                  </td>
                </tr>
              )}
              {bots && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                    {zh ? "還沒有任何網站聊天機器人。" : "There are no website chatbots yet."}
                  </td>
                </tr>
              )}
              {rows.map((bot) => (
                <tr key={bot.bot_id} className="hover:bg-gray-50">
                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${bot.deleted ? "text-gray-500" : "text-gray-900"}`}
                      >
                        {bot.bot_name || bot.bot_id}
                      </span>
                      {bot.deleted && (
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                          {zh ? "已刪除" : "Deleted"}
                        </span>
                      )}
                    </div>
                    {(bot.creator_name || bot.creator_email) && (
                      <p className="text-xs text-gray-500">
                        {zh ? "建立者：" : "Created by "}
                        {bot.creator_name || bot.creator_email}
                      </p>
                    )}
                  </td>
                  <td className={td}>
                    {bot.monthly_limit_usd === null ? (
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800">
                        {zh ? "未設定額度" : "No limit"}
                      </span>
                    ) : (
                      formatMoney(bot.monthly_limit_usd)
                    )}
                  </td>
                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <span className="min-w-[7rem] tabular-nums">
                        {formatMoney(bot.cycle_used_usd)}
                      </span>
                      <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 rounded-full"
                          style={{
                            width: `${maxSpend > 0 ? (bot.cycle_used_usd / maxSpend) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </td>
                  {isCurrent && (
                    <td className={`${td} tabular-nums`}>
                      <span className={bot.over_quota ? "font-semibold text-red-600" : ""}>
                        {formatMoney(bot.week_used_usd)}
                      </span>
                      <span className="text-gray-500">
                        {" / "}
                        {bot.this_week_quota_usd === null ? "—" : formatMoney(bot.this_week_quota_usd)}
                      </span>
                      {bot.over_quota && (
                        <span className="ml-2 inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-700">
                          {enforced
                            ? zh
                              ? "已封鎖"
                              : "Blocked"
                            : zh
                              ? "已超過額度"
                              : "Over quota"}
                        </span>
                      )}
                    </td>
                  )}
                  <td className={`${td} tabular-nums`}>{bot.cycle_turns}</td>
                  {canEdit && (
                    <td className={`${td} text-right`}>
                      {!bot.deleted && (
                        <button
                          type="button"
                          onClick={() => setEditing(bot)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-orange-600 border border-orange-200 hover:bg-orange-50"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" aria-hidden="true" />
                          {zh ? "編輯額度" : "Edit limit"}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <BotLimitModal
          bot={editing}
          initialCurrency={currency}
          rateState={rateState}
          currentUser={currentUser}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onSaved();
          }}
          language={language}
        />
      )}
    </div>
  );
}

// Set or remove one chatbot's monthly limit. Its quota changes immediately.
function BotLimitModal({
  bot, currentUser, onClose, onSaved, language, initialCurrency, rateState,
}) {
  const zh = language === "zh";
  const current = bot.monthly_limit_usd;
  const name = bot.bot_name || bot.bot_id;
  const draft = useLimitAmount({ initialUsd: current, initialCurrency, rateState });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const save = async (limit) => {
    setSaving(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/v2/usage-billing/admin/bots/${encodeURIComponent(bot.bot_id)}/limit`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ monthly_usage_limit_usd: limit }),
        },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      onSaved();
    } catch (err) {
      console.error("Failed to save chatbot usage limit:", err);
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={saving ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bot-limit-title"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <h3 id="bot-limit-title" className="text-lg font-bold text-gray-900">
            {zh ? `${name} 的每月額度` : `Monthly limit for ${name}`}
          </h3>
          <p className="text-sm text-gray-500">
            {zh
              ? "這個聊天機器人每 28 天的週期可用的金額，所有訪客共用，每週可用四分之一。"
              : "What this chatbot gets per 28-day cycle, shared by all its visitors; a quarter of it each week."}
          </p>
        </div>

        <form
          className="p-6 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.canSave && !saving) save(draft.usdLimit);
          }}
        >
          <LimitAmountField
            id="bot-limit"
            label={zh ? "每月額度" : "Monthly limit"}
            draft={draft}
            rateState={rateState}
            saving={saving}
            language={language}
            autoFocus
          />
          {draft.isZero && (
            <p className="text-xs text-amber-700">
              {zh
                ? "0 表示限制開啟後，這個聊天機器人將不再回答訪客。"
                : "0 stops this chatbot answering visitors once enforcement is on."}
            </p>
          )}
          <p className="text-xs text-gray-500">
            {zh ? "立即生效。" : "Applies immediately."}
          </p>
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-between gap-3 pt-3">
            {current !== null ? (
              confirmRemove ? (
                <span className="flex items-center gap-2 text-xs text-gray-600">
                  {zh ? "移除後將不受限制。" : "The chatbot will have no limit."}
                  <button
                    type="button"
                    onClick={() => save(null)}
                    disabled={saving}
                    className="font-medium text-red-600 hover:underline disabled:opacity-50"
                  >
                    {zh ? "移除" : "Remove"}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRemove(true)}
                  disabled={saving}
                  className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50"
                >
                  {zh ? "移除額度" : "Remove limit"}
                </button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                {zh ? "取消" : "Cancel"}
              </button>
              <button
                type="submit"
                disabled={!draft.canSave || saving}
                className="px-5 py-2 rounded-xl text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white shadow-sm disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? (zh ? "儲存中..." : "Saving...") : zh ? "儲存" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
